/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { UserContext } from '@gh/common-shared';
import { APP_NAME, ENVIRONMENT, REGION_CODE, VERSION } from '@gh/config';
import { TranslationService } from '@gh/core-mls';
import { Clipboard, WaitingService } from '@gh/core-ui';
import { flatten, repeat } from 'lodash';

function formatAsText(entries: string[][]): string {
  return flatten(entries.map(([label, value]) => [
    label,
    ': ',
    value,
    '\n',
  ])).join('');
}

function formatAsMonospaceText(entries: string[][]): string {
  const labelMaxLength = entries.reduce(
    (maxLength, [label]) => label.length > maxLength ? label.length : maxLength,
    0);
  return flatten(entries.map(([label, value]) => [
    label,
    ': ',
    repeat(' ', labelMaxLength - label.length),
    value,
    '\n',
  ])).join('');
}

function formatAsMarkdown(entries: string[][]): string {
  return flatten(entries.map(([label, value]) => [
    '|',
    label,
    '|',
    value,
    '|\n',
  ])).join('');
}

@Component({
  standalone: false,
  selector: 'gh-system-info-dialog',
  templateUrl: 'system-info-dialog.component.html',
  styleUrls: ['system-info-dialog.component.scss'],
})
export class SystemInfoDialogComponent implements OnInit {
  applicationName: string = APP_NAME;
  feBuildNumber: string = VERSION;
  beBuildNumber: string;
  environment: string = ENVIRONMENT;
  region: string = REGION_CODE;

  constructor(private waitingService: WaitingService,
              private dialogRef: MatDialogRef<any>,
              private userContext: UserContext,
              private translations: TranslationService,
              private clipboard: Clipboard) {
  }

  ngOnInit(): void {
    this.beBuildNumber = this.userContext.data.backendBuildNumber;
  }

  onCopyToClipboard(type: 'text' | 'monospace' | 'markdown'): void {
    const entries = [
      [this.translations.get('lbl_environment'), this.environment],
      [this.translations.get('lbl_application_name'), this.applicationName],
      [this.translations.get('lbl_region'), this.region],
      [this.translations.get('lbl_frontend_build_number'), this.feBuildNumber],
      [this.translations.get('lbl_backend_build_number'), this.beBuildNumber],
    ];

    switch (type) {
      case 'text':
        this.clipboard.copy(formatAsText(entries));
        break;
      case 'monospace':
        this.clipboard.copy(formatAsMonospaceText(entries));
        break;
      case 'markdown':
        this.clipboard.copy(formatAsMarkdown(entries));
        break;
      default:
        break;
    }
  }

  close(): void {
    this.dialogRef.close();
  }
}
